import { z } from 'zod';
// Carrier recipes, `plancia.recipe/v3` (03 §3.3): the steps the extension
// replays on a carrier's site to reach the results page, as data. No carrier
// code lives in the extension: a recipe is the whole knowledge of a site.
//
// v3 is v1's `dokless.tracking-plan.v2` (estensione/lib/plan.js,
// page/steps.js) in English, with the same semantics:
//
//   open_url        load `url` (every recipe starts here: a fresh page, or the
//                   wait could read the previous search's results — Messina)
//   refuse_cookies  click a «reject» button, or hide the banner; never accept
//   select          set a <select> to `value`
//   select_custom   open `trigger_selector`, click the option whose text is `option_text`
//   type            type `text` key by key; `submit` presses Enter (v1 `invio`)
//   click           by `selector`, or by visible `text` (optionally on a `tag`)
//   wait_for        until one of `any` shows (text or selector); an option with
//                   `outcome: not_found` must hold `confirm_not_found_ms` (pages
//                   show «0 results» while loading — ONE, 313 ms)
//   wait_challenge_clear  a PASSIVE «verification in progress» page (CMA CGM)
//                   that clears by itself: wait without touching anything, up
//                   to `timeout_ms` (60 s at most; default 30 s); still there, or asking
//                   for anything (checkbox, images, a question) → stop, a
//                   person is needed (ADR 0043, ADR 0061)
//   expand_rows     click every closed «+» (`selector`, `text`, or
//                   `text_pattern` for a label with a count in it: Maersk's
//                   «View 9 events completed from origin»), up to `max_rounds`
//   sleep           wait `ms`
//   scroll          to a `fraction` of the page (v1 `frazione`), or `y` px, or `to` top/bottom
//   screenshot      keep a picture of the page
//   scan            read the page and send it (`html: true` also sends the HTML)
//   for_each_number only as the FIRST step (then `open_url`): the steps after
//                   it run once per number of the shipment — the carrier's
//                   B/L and booking, every container, the other B/Ls and
//                   bookings — in the same tab, each number with its kind,
//                   at most `max_numbers` (6), `pause_ms` between searches;
//                   a wall stops the visit at once (owner, 20/10; ADR 0068)
//
// Placeholders, filled by the extension: `{reference}` (the number searched),
// `{reference_type.key}` and `{reference_type.<field>}` (from
// `reference_types`), `{choice}` (a per-shipment choice such as Akkon's
// country of loading, v1 `{scelta}`), `{origin_country}` (the country of
// Dokless's place of loading as the carrier's menu names it, from its
// UN/LOCODE: Akkon v5), `{start_url}`, `{search_container}` (the container
// searched, empty for a B/L or booking) and `{search_bl_or_booking}` (the B/L
// or booking searched, or the one paired with a container by `pair_with`:
// Akkon v6).
export const RECIPE_SCHEMA = 'plancia.recipe/v3';
/** `for_each_number`: at most this many searches in one visit (owner, 20/10). */
export const MAX_NUMBERS_PER_READ = 6;
/** `for_each_number`: the default pause between two searches of one visit. */
export const NUMBER_PAUSE_MS = 3_000;
/** v1 formats the upgrade accepts. */
export const V1_RECIPE_SCHEMAS = ['dokless.tracking-plan.v2', 'dokless.tracking-plan.v1'];
/** What a recipe can search by: the carrier keys, and vessel names for tools (VesselFinder). */
export const RECIPE_REFERENCE_KINDS = ['container', 'bl', 'booking', 'vessel_name'];
const selector = z.string().min(1).max(512);
const label = z.string().max(200).optional();
const index = z.int().min(0).max(50).optional();
const waitOptionSchema = z
    .object({
    text: z.string().min(1).max(200).optional(),
    selector: selector.optional(),
    /**
     * `invalid`: the site refused the search itself (Akkon's «Enter your Booking / Container Number»): an
     * invalid search for that number, neither results nor «not found».
     */
    outcome: z.enum(['found', 'not_found', 'invalid']).optional(),
    /** With `invalid`: the visible text of the site dialog's button that closes it (it accepts nothing). */
    dismiss_text: z.string().min(1).max(40).optional(),
})
    .refine((option) => option.text !== undefined || option.selector !== undefined, 'text or selector');
export const recipeStepSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('open_url'), url: z.string().min(1).max(2048), label }),
    z.object({ action: z.literal('refuse_cookies'), selector: selector.optional(), label }),
    z.object({ action: z.literal('select'), selector, value: z.string().max(200), index, label }),
    z.object({
        action: z.literal('select_custom'),
        trigger_selector: selector,
        option_text: z.string().min(1).max(200),
        option_selector: selector.optional(),
        label,
    }),
    z.object({
        action: z.literal('type'),
        selector,
        text: z.string().max(200),
        submit: z.boolean().optional(),
        index,
        label,
    }),
    z
        .object({
        action: z.literal('click'),
        selector: selector.optional(),
        text: z.string().min(1).max(200).optional(),
        tag: z.string().min(1).max(32).optional(),
        index,
        label,
    })
        .refine((step) => step.selector !== undefined || step.text !== undefined, 'selector or text'),
    z.object({
        action: z.literal('wait_for'),
        any: z.array(waitOptionSchema).min(1).max(20),
        timeout_ms: z.int().positive().max(120_000).optional(),
        confirm_not_found_ms: z.int().min(0).max(60_000).optional(),
        optional: z.boolean().optional(),
        label,
    }),
    z.object({
        action: z.literal('expand_rows'),
        selector: selector.optional(),
        text: z.string().min(1).max(200).optional(),
        /** Case-insensitive regular expression on the element's text (a label that carries a count). */
        text_pattern: z.string().min(1).max(200).optional(),
        max_rounds: z.int().min(1).max(20).optional(),
        pause_ms: z.int().min(0).max(10_000).optional(),
        label,
    }),
    z.object({
        action: z.literal('wait_challenge_clear'),
        /** How long a passive challenge may stay (default and maximum 30 s). */
        timeout_ms: z.int().positive().max(60_000).optional(),
        label,
    }),
    z.object({ action: z.literal('sleep'), ms: z.int().positive().max(60_000), label }),
    z.object({
        action: z.literal('scroll'),
        fraction: z.number().min(0).max(1).optional(),
        y: z.int().min(0).optional(),
        selector: selector.optional(),
        to: z.enum(['top', 'bottom']).optional(),
        ms: z.int().min(0).max(10_000).optional(),
        label,
    }),
    z.object({ action: z.literal('screenshot'), label }),
    z.object({ action: z.literal('scan'), html: z.boolean().optional(), label }),
    z.object({
        action: z.literal('for_each_number'),
        max_numbers: z.int().min(1).max(MAX_NUMBERS_PER_READ).optional(),
        pause_ms: z.int().min(0).max(30_000).optional(),
        label,
    }),
]);
export const recipeReferenceTypeSchema = z.object({
    /** Regular expression a number must match to be tried with this key. */
    pattern: z.string().min(1).max(200).optional(),
    /** Site-specific name of the field or option for this key (v1 `campo`), for `{reference_type.field}`. */
    field: z.string().min(1).max(100).optional(),
    /** Value of the site's type selector for this key. */
    select_value: z.string().min(1).max(100).optional(),
    /**
     * The URL's value for this key, for `{reference_type.url_value}` (v1
     * `url_value`, agent/rules.ts): COSCO's `trackingType=CONTAINER|BILLOFLADING|BOOKING`.
     */
    url_value: z.string().min(1).max(100).optional(),
    /**
     * The site searches this kind only beside another number of the shipment (Akkon: a container only with
     * the booking, else the B/L, in «BL/Booking No»): the first of these kinds the shipment has. Without one
     * the number is not searched (ADR 0095).
     */
    pair_with: z
        .array(z.enum(['booking', 'bl']))
        .min(1)
        .max(2)
        .optional(),
    label: z.string().max(100).optional(),
});
export const recipeResultsSchema = z.object({
    /** Texts that mean «this number does not exist here» (from the `wait_for` not-found options). */
    not_found_text: z.array(z.string()).optional(),
    /** Where structured data is on the page, when the site has it (a JSON endpoint seen by the network hook). */
    structured_source: z.string().nullable().optional(),
    /** On «not found», try the reference types whose pattern does not match too (v1, Hyundai). */
    try_other_types_on_not_found: z.boolean().optional(),
});
export const recipeLearnedFromSchema = z.object({
    by: z.enum(['human', 'model', 'claude']),
    /** The model that learned it (v1 `qwen`), when `by` is not `human`. */
    model: z.string().max(64).nullable().optional(),
    reference: z.string().max(64).nullable().optional(),
    at: z.string().nullable().optional(),
    read_id: z.string().nullable().optional(),
    from_version: z.int().min(1).optional(),
});
/** A whole `plancia.recipe/v3` document (what an API client sends and receives). */
export const recipeDocumentSchema = z.object({
    schema: z.literal(RECIPE_SCHEMA),
    carrier: z.object({ code: z.string().min(1).max(64) }),
    reference_types: z
        .partialRecord(z.enum(RECIPE_REFERENCE_KINDS), recipeReferenceTypeSchema)
        .refine((types) => Object.keys(types).length > 0, 'at least one reference type'),
    steps: z.array(recipeStepSchema).min(1).max(60),
    results: recipeResultsSchema.optional(),
    learned_from: recipeLearnedFromSchema.optional(),
});
//# sourceMappingURL=recipe.schema.js.map